import React from "react";

import { Button, Card, Loading, Page, ProgressBar, SkeletonBodyText } from "@shopify/polaris";
import ArtifactElementCard from "../ArtifactElementCard";
import MarkAsCompleteModal from "../MarkAsCompleteModal/MarkAsCompleteModal";
import InspectionCompleteModal from "../InspectionCompleteModal/InspectionCompleteModal";
import MarkAsCompleteHeader from "../MarkAsCompleteHeader/MarkAsCompleteHeader";
import styles from "./Sections.module.css";
import documentStyle from "./InspectionDocuments.module.css";
import { MainFrameContext } from "../contexts/MainFrameContext";
import MainFrameContextType from "../../types/MainFrameContextType";
import ArtifactViewer from "../ArtifactViewer";
import { ElementsContext } from "../contexts/ElementsContext";
import ElementsContextType from "../../types/ElementsContextType";
import { useDispatch, useSelector } from "react-redux";
import { PortalState } from "../../types/PortalState";
import InspectionElement, { PdfChecklistValidationType } from "../../types/InspectionElement";
import { getInspectionById } from "../../store/actions/Inspections";
import { AssetType } from "../../types/AssetType";
import SurveyorCompletionStatus from "../../types/SurveyorCompletionStatus";
import ArtifactGallery from "../ArtifactGallery";
import { WarningBanner } from "../WarningBanner/WarningBanner";
import GuidenceList from "../GuidenceList/GuidenceList";
import CarbonSection from "../CarbonSection";
import {
    ImportMinor
} from '@shopify/polaris-icons';
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ElementDocument from "../../types/ElementDocument";
import {userIsDocCollector, userIsInAppGroup} from "../../helpers/users";
import EmbarkDates from "../EmbarkDates/EmbarkDates";
import { ReusableDocuments } from "../../pages/ReusableDocuments";
import { showDocumentReuse } from "../../helpers/utils";
import DocumentSharedDatePicker from "../DocumentSharedDatePicker/DocumentSharedDatePicker";
import AppChecklistCard from "../ArtifactElementCard/AppChecklistCard";

interface Props {
    id: string;
    assetType: AssetType;
}

export default function Sections({ id, assetType }: Props) {
    const { setShowSideBar, anchor } = React.useContext(
        MainFrameContext
    ) as MainFrameContextType;
    const [showMarkAsCompleteModal, setShowMarkAsCompleteModal] =
        React.useState(false);
    const {
        focusedInspectionElement,
        focusedImage,
        showImageViewer,
        showGallery,
        setShowGallery
    } = React.useContext(ElementsContext) as ElementsContextType;

    const [downloadingZip, setDownloadingZip] = React.useState(false);
    const [downloadPercentage, setDownloadPercentage] = React.useState(0);
    const isDocumentCollector: boolean = useSelector((state: any) => userIsDocCollector(state));
    const isInAppGroup: boolean = useSelector((state: any) => userIsInAppGroup(state));
    const appChecklist = useSelector((state: PortalState) => state.portal.activeInspection?.appChecklist ?? null);
    const showAppChecklist = isInAppGroup && !!appChecklist;


    const currentInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );
    const surveyorCompletionStatus: SurveyorCompletionStatus | undefined =
        useSelector(
            (state: PortalState) =>
                state.portal.activeInspection?.SurveyorCompletionStatus
        );

    const inspectionDocumentReuseStatus = useSelector(
        (state: PortalState) => state.portal.activeInspection?.DocumentReuseStatus
    );

    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(getInspectionById(id));
        setShowSideBar(true);
    }, []);


    if (showGallery && anchor === "#landing") {
        setShowGallery(false);
    }

    function openModal() {
        setShowMarkAsCompleteModal(true);
    }
    const loading = currentInspection === undefined;

    let elements: InspectionElement[] = [];

    let sectionComplete = false;

    if (
        !loading &&
        currentInspection !== undefined &&
        surveyorCompletionStatus !== undefined
    ) {
        elements = currentInspection.sections;

        if (!isDocumentCollector) {
            if (assetType === AssetType.Document) {
                sectionComplete = surveyorCompletionStatus.DocumentsComplete;
            }
            if (assetType === AssetType.Image) {
                sectionComplete = surveyorCompletionStatus.PhotosComplete;
            }
            if (assetType === AssetType.CheckList) {
                sectionComplete = surveyorCompletionStatus.ChecklistComplete;
            }
            if (assetType === AssetType.CarbonOffsetting) {
                sectionComplete = surveyorCompletionStatus.CarbonSectionComplete;
            }
        }
    }

    const sectionTitles = [
        "N/A",
        "Documents",
        "Photos",
        "Carbon Offsetting",
        "Inspection Checklists",
        "Others"
    ];
    const title = sectionTitles[assetType];

    const sectionElements = elements.filter((element) => {
        return element.ElementType === assetType;
    });

    let sectionStyle = styles;

    if (assetType === AssetType.Document) {
        sectionStyle = documentStyle;
    }

    let dueDate = "";
    if (currentInspection !== undefined) {
        dueDate = currentInspection.DueDate;
    }

    const downloadAllDocuments = async () => {
        setDownloadingZip(true);
        const listOfDocuments = currentInspection?.sections.filter(e => e.Documents.length > 0).flatMap(e => e.Documents);
        if (!listOfDocuments) {
            return;
        }

        await downloadZip(listOfDocuments);
    }

    const downloadZip = async (documents: ElementDocument[]) => {
        const zip = new JSZip();
        const folder : JSZip | null = null;

        if (documents.length === 0) {
            return;
        }

        const downloadPercentTick = 100 / documents.length;

        const blobFetcher = await Promise.all(
            documents.map(async (document, index) => {
                const res = await fetch(document.url);
                const blob = res.blob();

                setDownloadPercentage(index * downloadPercentTick);
                return blob;
            })
        );

        const fileNames: string[] = [];

        blobFetcher.forEach((blob, index) => {
            const fileName = documents[index].name;

            if (!fileNames.includes(fileName)) {
                (folder ?? zip).file(fileName, blob);
                fileNames.push(fileName);
            } else {
                let adjustIterator = 1;
                const fileNameWithoutExtension = fileName.substring(0, fileName.lastIndexOf("."))
                const ext = fileName.substring(fileName.lastIndexOf("."));

                let adjustedFileName: string;
                do {
                    adjustedFileName = `${fileNameWithoutExtension}_${adjustIterator++}${ext}`;
                } while (fileNames.includes(adjustedFileName));
                (folder ?? zip).file(adjustedFileName, blob);
                fileNames.push(adjustedFileName);
            }

        });

        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "Documents.zip");

        setDownloadingZip(false);
    }

    const downloadAllButton = () => {
        if (currentInspection?.NumberOfDocuments === 0) {
            return <div />;
        }

        return (
            <Button
                primary
                icon={ImportMinor}
                onClick={downloadAllDocuments}
                data-cy="downloadAllDocumentsBtn">
                Download All
            </Button>
        )
    }

    const downloadProgress = () => {
        return (
            <div style={{width: 225}}>
                <span className={styles.preparingDownload}>Preparing download...</span>
                <ProgressBar progress={downloadPercentage} />
            </div>
        );
    }

    const downloadOrProgress = () => {
        return (downloadingZip ? downloadProgress() : downloadAllButton() );
    }

    /**
     * I think this file/method has reached the end of it's life
     * This should be the last section we add without a refactor
     * This method returns HTML content depending on which section type
     * It's a bit hard to understand what's going on in here
     * @returns
     */
    function renderSection() {
        if (assetType === AssetType.CarbonOffsetting) {
            return <CarbonSection />;
        }

        if (assetType === AssetType.CheckList) {
            const appChecklistElements = sectionElements.filter(
                (e) => !e.Guideline && e.PdfChecklistValidation === PdfChecklistValidationType.AppChecklist
            );
            const otherChecklistElements = sectionElements.filter(
                (e) => !e.Guideline && e.PdfChecklistValidation !== PdfChecklistValidationType.AppChecklist
            );
            return (
                <article className={sectionStyle.twoColumnLayout}>
                    {showAppChecklist && appChecklist && (
                        <div className={sectionStyle.column}>
                            <AppChecklistCard appChecklist={appChecklist} />
                        </div>
                    )}
                    {!showAppChecklist && appChecklistElements.map((element) => (
                        <div key={element.Id} className={sectionStyle.column}>
                            <ArtifactElementCard assetType={assetType} element={element} />
                        </div>
                    ))}
                    {otherChecklistElements.map((element) => (
                        <div key={element.Id} className={sectionStyle.column}>
                            <ArtifactElementCard assetType={assetType} element={element} />
                        </div>
                    ))}
                </article>
            );
        }

        return (
            <article className={sectionStyle.twoColumnLayout}>
                {sectionElements
                    .filter((e) => !e.Guideline)
                    .map((element) => (
                        <div key={element.Id} className={sectionStyle.column}>
                            <ArtifactElementCard assetType={assetType} element={element} />
                        </div>
                    ))}
            </article>
        );
    }

    function cardSelectionHtml() {
        if (showDocumentReuse(assetType,  isDocumentCollector, inspectionDocumentReuseStatus, elements)) {
            return (
                <>
                    {(loading || sectionComplete) ? null : (
                        <div className={styles.completeHeader}>
                            <MarkAsCompleteHeader
                                inspectionEnd={dueDate}
                                openModal={openModal}
                            />
                        </div>
                    )}
                    <div className={
                        sectionComplete
                            ? styles.guidenceTitleNoGap
                            : styles.guidenceTitle
                    }>
                        <ReusableDocuments inspectionId={currentInspection?.Id ?? ""} inspectionElements={elements} />
                    </div>
                </>
            )
        }
        return (
            <div>
                <MarkAsCompleteModal
                    type={assetType}
                    active={showMarkAsCompleteModal}
                    closeModal={() => {
                        setShowMarkAsCompleteModal(false);
                    }}
                    appChecklistRequired={showAppChecklist}
                    appChecklistSubmitted={appChecklist?.status === 'submitted'}
                />
                <InspectionCompleteModal />

                {(loading || sectionComplete) ? null : (
                    <div className={styles.completeHeader}>
                        <MarkAsCompleteHeader
                            inspectionEnd={dueDate}
                            openModal={openModal}
                        />
                    </div>
                )}
                <WarningBanner />

                <Page fullWidth>
                    {assetType === AssetType.CheckList ? (
                        <div>
                            <h1
                                className={
                                    sectionComplete
                                        ? styles.guidenceTitleNoGap
                                        : styles.guidenceTitle
                                }
                            >
                                Guidance Notes
                            </h1>
                            <GuidenceList />
                            {!isDocumentCollector ? (
                                <>
                                    <h1 className={
                                        styles.guidenceTitleNoGap
                                    }>Embark / Disembark Dates <span style={{color: "red", fontSize: "16px"}}>(Required)</span></h1>
                                    <EmbarkDates />
                                </>
                            ) : <></>}
                        </div>
                    ) : null}

                    {currentInspection && currentInspection?.NotesForSurveyor && !isDocumentCollector ? (
                        <div className={styles.surveyorNotesContainer}>
                            <h1>Notes</h1>
                            <Card>
                                <div className={styles.surveyorNotes }>
                                    {currentInspection.NotesForSurveyor}
                                </div>
                            </Card>
                        </div>
                    ) : null}

                    <DocumentSharedDatePicker/>

                    <div className={styles.pageTitleContainer}>
                        {assetType === AssetType.CheckList ? (
                            sectionElements.filter(element => !element.Guideline).length > 0 ?
                                <h1 className={styles.checklistTitle}>{title}</h1>
                                :
                                <></>
                        ) : (
                            <h1
                                className={
                                    sectionComplete
                                        ? styles.pageTitleNoGap
                                        : styles.pageTitle
                                }
                            >
                                {title}
                            </h1>
                        )}
                        {assetType === AssetType.Document
                            ? <div className={`${styles.downloadBtnTop}`}>{downloadOrProgress()}</div>
                            : <div />}
                        {sectionComplete ? (
                            <h1>Section Marked as Complete</h1>
                        ) : (
                            <div />
                        )}
                    </div>

                    {loading ? (
                        <div>
                            <Loading />
                            <Card sectioned>
                                <SkeletonBodyText />
                            </Card>
                            <Card sectioned>
                                <SkeletonBodyText />
                            </Card>
                        </div>
                    ) : (
                        renderSection()
                    )}
                    {assetType === AssetType.Document
                        ? <div className={styles.downloadBtnBottom}>
                            {downloadOrProgress()}
                        </div>
                        : <div />}
                </Page>
            </div>
        );
    }

    function artifactGalleryHtml() {
        if (focusedInspectionElement) {
            return (
                <div>
                    <Page title={focusedInspectionElement?.Name} fullWidth>
                        <article>
                            {[...elements]
                                .filter(
                                    (x) => x.Id === focusedInspectionElement.Id
                                )
                                .map((element) => (
                                    <ArtifactGallery
                                        key={element.Name}
                                        data={element}
                                    />
                                ))}
                        </article>
                    </Page>
                </div>
            );
        }
    }

    function renderHtmlContent() {
        if (showImageViewer && focusedInspectionElement && focusedImage) {
            return (
                <ArtifactViewer
                    type={assetType}
                    focalImage={focusedImage}
                    dataId={focusedInspectionElement.Id}
                />
            );
        }

        if (showGallery) {
            return artifactGalleryHtml();
        }

        return cardSelectionHtml();
    }

    return <div>{renderHtmlContent()}</div>;
}
